// Types for bank reconciliation: staged "bank incoming" lines and the promoted
// settled-ledger row a match produces.

import type { TransactionType } from './captable';
import type { DealCostType } from './deal-costs';
import type { FundCostCategory } from './fund-costs';
import type {
  FundTransactionStatus,
  FundTransactionType,
} from './fund-transactions';

export type IncomingMatchStatus =
  'UNMATCHED' | 'PARTIAL_MATCHED' | 'MATCHED' | 'INTERNAL_TRANSFER' | 'IGNORED';

export const INCOMING_MATCH_STATUS_LABELS: Record<IncomingMatchStatus, string> =
  {
    UNMATCHED: 'Awaiting match',
    PARTIAL_MATCHED: 'Partially matched',
    MATCHED: 'Matched',
    INTERNAL_TRANSFER: 'Internal transfer',
    IGNORED: 'Ignored',
  };

// The named evidence a suggestion rests on. The first three are strong enough
// for the batch to book without a human; the rest always wait for one.
export type MatchBasis =
  | 'REFERENCE_IBAN_AMOUNT'
  | 'REFERENCE_AND_AMOUNT'
  | 'IBAN_AND_AMOUNT'
  | 'REFERENCE_ONLY'
  | 'IBAN_AND_SUM_OF_EXPECTATIONS'
  | 'IBAN_AND_PARTIAL_AMOUNT'
  | 'IBAN_ONLY'
  | 'FUND_MANAGER_IBAN'
  | 'AMOUNT_ONLY'
  | 'RULE'
  | 'COUNTERPARTY_MEMORY'
  | 'KNOWN_FUND_COST_PAYEE'
  | 'KNOWN_DEAL_COST_PAYEE';

export const MATCH_BASIS_LABELS: Record<MatchBasis, string> = {
  REFERENCE_IBAN_AMOUNT: 'Reference + IBAN + amount',
  REFERENCE_AND_AMOUNT: 'Reference + amount',
  IBAN_AND_AMOUNT: 'IBAN + amount',
  REFERENCE_ONLY: 'Reference only',
  IBAN_AND_SUM_OF_EXPECTATIONS: 'Covers several items',
  IBAN_AND_PARTIAL_AMOUNT: 'Partial payment',
  IBAN_ONLY: 'IBAN only',
  FUND_MANAGER_IBAN: 'Fund manager IBAN',
  AMOUNT_ONLY: 'Amount only',
  RULE: 'Matching rule',
  COUNTERPARTY_MEMORY: 'Counterparty history',
  KNOWN_FUND_COST_PAYEE: 'Earlier fund cost',
  KNOWN_DEAL_COST_PAYEE: 'Earlier deal cost',
};

export type ReconciliationAction =
  | 'RELEASED'
  | 'AUTO_APPLIED'
  | 'SUGGESTED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'MANUALLY_MATCHED'
  | 'MARKED_INTERNAL_TRANSFER'
  | 'IGNORED'
  | 'RELEASED';

export const RECONCILIATION_ACTION_LABELS: Record<
  ReconciliationAction,
  string
> = {
  RELEASED: 'Returned to the inbox',
  AUTO_APPLIED: 'Booked automatically',
  SUGGESTED: 'Suggested',
  ACCEPTED: 'Suggestion accepted',
  REJECTED: 'Suggestion rejected',
  MANUALLY_MATCHED: 'Matched by hand',
  MARKED_INTERNAL_TRANSFER: 'Marked internal transfer',
  IGNORED: 'Ignored by rule',
};

// One fund transaction a matched line settled. A line split across several
// items has one of these per allocation.
export interface MatchedTarget {
  fund_transaction_id: string;
  fund_transaction_type: FundTransactionType;
  description: string | null;
  transaction_date: string;
  transaction_amount: number;
  applied_amount: number;
  investor_name: string | null;
}

export interface SuggestionAlternative {
  basis: MatchBasis;
  explanation: string;
  fund_transaction_id: string | null;
}

export interface BankIncomingAuditEntry {
  id: string;
  action: ReconciliationAction;
  basis: MatchBasis | null;
  explanation: string | null;
  fund_transaction_id: string | null;
  actor_user_id: string | null;
  rule_id: string | null;
  rule_version: number | null;
  engine_version: string;
  created_at: string;
}

export type BankTransactionType =
  | 'SEPA_CREDIT_TRANSFER'
  | 'CREDIT_TRANSFER'
  | 'SEPA_DIRECT_DEBIT'
  | 'DIRECT_DEBIT'
  | 'CARD_PAYMENT'
  | 'CASH_WITHDRAWAL'
  | 'CASH_DEPOSIT'
  | 'CHEQUE'
  | 'BANK_FEE'
  | 'INTEREST'
  | 'TAX'
  | 'RETURN'
  | 'REVERSAL'
  | 'FX_TRADE'
  | 'OTHER'
  | 'UNKNOWN';

export const BANK_TRANSACTION_TYPE_LABELS: Record<BankTransactionType, string> =
  {
    SEPA_CREDIT_TRANSFER: 'SEPA credit transfer',
    CREDIT_TRANSFER: 'Credit transfer',
    SEPA_DIRECT_DEBIT: 'SEPA direct debit',
    DIRECT_DEBIT: 'Direct debit',
    CARD_PAYMENT: 'Card payment',
    CASH_WITHDRAWAL: 'Cash withdrawal',
    CASH_DEPOSIT: 'Cash deposit',
    CHEQUE: 'Cheque',
    BANK_FEE: 'Bank fee',
    INTEREST: 'Interest',
    TAX: 'Tax',
    RETURN: 'Return',
    REVERSAL: 'Reversal',
    FX_TRADE: 'FX trade',
    OTHER: 'Other',
    UNKNOWN: 'Unknown',
  };

// A staged bank-feed line awaiting reconciliation, surfaced in the GL as
// "bank incoming" so an operator can match it to a fund transaction.
export interface BankIncomingLine {
  id: string;
  fund_bank_account_id: string;
  amount: number;
  currency: string | null;
  value_date: string | null;
  execution_date: string | null;
  counterparty_name: string | null;
  counterparty_reference: string | null;
  remittance_information: string | null;
  end_to_end_id: string | null;
  bank_transaction_code: string | null;
  proprietary_bank_transaction_code: string | null;
  bank_transaction_type: BankTransactionType;
  match_status: IncomingMatchStatus;
}

// A staged line as shown in the admin module
export interface BankIncomingAdminLine extends BankIncomingLine {
  fund_id: string;
  fund_name: string;
  // A foreign line cannot be a deal cost: cash 1010 is base-currency only.
  is_in_fund_base_currency: boolean;
  account_name: string;
  bank_name: string;
  account_iban: string | null;
  source: string;
  external_id: string | null;
  remittance_information_type: string | null;
  fund_bank_account_transaction_id: string | null;
  fund_transaction_id: string | null;
  fund_transaction_type: FundTransactionType | null;
  matched_targets: MatchedTarget[];

  // What the last reconciliation run proposed for this line, in plain words.
  suggested_fund_transaction_id: string | null;
  suggestion_basis: MatchBasis | null;
  suggestion_note: string | null;
  suggestion_alternatives: SuggestionAlternative[];
  suggested_allocations: SuggestedAllocation[];
  suggested_at: string | null;
  allocated_amount: number;
  unapplied_amount: number;
  allocations: LineAllocation[];
  payer_investor_id: string | null;
  payer_investor_name: string | null;
  // The fund's manager, when it has one: the line can be booked to its current account.
  fund_manager_entity_id: string | null;
  fund_manager_name: string | null;
  created_at: string;
}

export interface SuggestedAllocation {
  label: string;
  amount: number;
  drawdown_investor_id: string | null;
  fund_transaction_id: string | null;
}

export type AllocationResolution =
  'ALLOCATED' | 'UNAPPLIED' | 'MANAGER_CURRENT_ACCOUNT' | 'PAIRED' | 'RETURNED';

export interface LineAllocation {
  id: string;
  amount: number;
  resolution: AllocationResolution;
  drawdown_investor_id: string | null;
  fund_transaction_id: string | null;
  unapplied_investor_id: string | null;
  fund_manager_entity_id: string | null;
  counter_line_id: string | null;
  cleared_return_amount: number | null;
  created_at: string;
}

export interface BankIncomingSummary {
  unmatched_count: number;
  partly_applied_count: number;
  matched_count: number;
  internal_transfer_count: number;
  unmatched_amount_by_currency: Record<string, number>;
}

// The promoted settled-ledger row produced by a match. The match is recorded
// directly on the bank transaction (which fund transaction it settled, a note of
// how it was matched, by whom, and whether it was automatic).
export interface MatchedBankLine {
  id: string;
  fund_bank_account_id: string;
  amount: number;
  currency: string | null;
  matched_fund_transaction_id: string | null;
  match_note: string | null;
  matched_by_user_id: string | null;
  matched_at: string | null;
  is_auto: boolean | null;
}

export type CashDirection = 'CREDIT' | 'DEBIT';

// Why a candidate cannot be matched as it stands.
export type MatchBlockedReason =
  | 'amount_exceeds_remaining'
  | 'transaction_already_paid'
  | 'held_by_captable_transaction';

export const MATCH_BLOCKED_REASON_LABELS: Record<MatchBlockedReason, string> = {
  amount_exceeds_remaining: 'More than the outstanding amount',
  transaction_already_paid: 'Nothing left to confirm',
  held_by_captable_transaction:
    'Spent on a cap table transaction - unlink it there to pair these lines',
};

export interface TransactionMatchCandidate {
  id: string;
  fund_bank_account_id: string;
  account_name: string;
  bank_name: string;
  source: string;
  external_id: string | null;
  amount: string;
  unapplied_amount: string;
  currency: string | null;
  value_date: string | null;
  execution_date: string | null;
  counterparty_name: string | null;
  counterparty_reference: string | null;
  remittance_information: string | null;
  end_to_end_id: string | null;
  bank_transaction_code: string | null;
  proprietary_bank_transaction_code: string | null;
  bank_transaction_type: BankTransactionType;
  suggested: boolean;
  suggestion_basis: MatchBasis | null;
  suggestion_note: string | null;
  can_match: boolean;
  blocked_reason: MatchBlockedReason | null;
}

export interface TransactionMatchedBankLine {
  id: string;
  fund_bank_account_id: string;
  account_name: string;
  bank_name: string;
  source: string;
  external_id: string | null;
  amount: string;
  currency: string | null;
  value_date: string | null;
  execution_date: string | null;
  counterparty_name: string | null;
  counterparty_reference: string | null;
  remittance_information: string | null;
  match_note: string | null;
  matched_by_user_id: string | null;
  matched_by_name: string | null;
  matched_at: string | null;
  is_auto: boolean | null;
  /** The line settled an LP payment, so the investor list already shows it. */
  settles_investor_payment: boolean;
}

export interface TransactionBankMatchView {
  fund_transaction_id: string;
  status: FundTransactionStatus;
  currency: string | null;
  amount: string;
  covered_amount: string;
  remaining_amount: string;
  direction: CashDirection | null;
  is_reconcilable: boolean;
  is_open: boolean;
  matched_lines: TransactionMatchedBankLine[];
  candidates: TransactionMatchCandidate[];
  unmatched_line_count: number;
  truncated: boolean;
}

// Which Flow object an open item was projected from. An LP payout is not here:
// a match records who paid only for a capital call, so a distribution is
// offered whole, as a fund payment, and never split per investor.
export type OpenItemKind =
  | 'LP_CONTRIBUTION'
  | 'FUND_PAYMENT'
  | 'PORTFOLIO_TRANSACTION'
  | 'BANK_LINE'
  | 'UNAPPLIED_CASH'
  | 'RETURNED_PAYMENT';

export const OPEN_ITEM_KIND_LABELS: Record<OpenItemKind, string> = {
  LP_CONTRIBUTION: 'Capital calls',
  FUND_PAYMENT: 'Fund payments',
  PORTFOLIO_TRANSACTION: 'Portfolio transactions',
  BANK_LINE: 'Opposite bank lines',
  UNAPPLIED_CASH: 'LP unapplied cash',
  RETURNED_PAYMENT: 'Returned payments',
};

export interface OpenItem {
  id: string;
  kind: OpenItemKind;
  label: string;
  reference: string | null;
  counterparty_name: string | null;
  counterparty_iban: string | null;
  due_date: string | null;
  outstanding: number;
  full_amount: number;
  currency: string | null;
  fund_transaction_id: string | null;
  fund_transaction_type: FundTransactionType | null;
  captable_transaction_id: string | null;
  captable_transaction_type: TransactionType | null;
  // Set only for LP-level items: the investor whose share this settles.
  drawdown_investor_id: string | null;
  // Set only for an opposite bank line this one can be paired with.
  counter_line_id: string | null;
  // Set only for an LP's unapplied cash money going out can refund.
  unapplied_investor_id: string | null;
  // Set only for a fund item this line could be a return of.
  returned_fund_transaction_id: string | null;
  // Set only on a counter line a cap table transaction still holds: where to
  // go to let it go. Not a target - it cannot be paired until then.
  held_by_captable_transaction_id: string | null;
  held_by_portfolio_company_id: string | null;
  suggested: boolean;
  can_match: boolean;
  blocked_reason: string | null;
}

/** The last fund cost to a payee, moved to start on the line's value date. */
export interface FundCostTemplate {
  cost_category: FundCostCategory;
  description: string | null;
  counterparty_name: string | null;
  notes: string | null;
  effective_from: string;
  effective_to: string;
}

/** The deal is left out: one adviser works on several deals. */
export interface DealCostTemplate {
  cost_type: DealCostType;
}

/** Null where the line's IBAN was never paid that kind of cost. */
export interface BankLineCostTemplate {
  fund_cost: FundCostTemplate | null;
  deal_cost: DealCostTemplate | null;
}
